import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
  PlusIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  UserPlusIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  PauseIcon
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import V2SendDistributionPreview from '../components/V2SendDistributionPreview';

const MassFollows = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [scrapedData, setScrapedData] = useState({ tags: [], sessions: [] });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    taskMode: 'legacy',
    accountIds: [],
    actionType: 'follow',
    recipientSource: 'scraped',
    manualRecipients: '',
    scrapedDataTag: '',
    settings: {
      batchPauseEnabled: true,
      delayMin: 30,
      delayMax: 60,
      followsPerBatchMin: 20,
      followsPerBatchMax: 20,
      batchPauseMin: 600,
      batchPauseMax: 600,
      restInterval: 600,
      spamPauseDuration: 3600,
      maxRequestsPerDayMin: 40,
      maxRequestsPerDayMax: 45,
      activeHoursPerDay: 12,
      unfollowAfterDays: null,
      unfollowAfterHours: null
    }
  });

  const { socket } = useSocket();

  // Track which dropdowns are open
  const [openDropdowns, setOpenDropdowns] = useState(new Set());
  
  const handleToggleDropdown = useCallback((taskId) => {
    setOpenDropdowns(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  // Batch socket updates
  const updateQueueRef = useRef(new Map());
  const updateTimeoutRef = useRef(null);

  useEffect(() => {
    loadTasks();
    loadAccounts();
    loadScrapedData();
    loadGroups();
  }, []);

  useEffect(() => {
    if (!socket) return;

    socket.emit('join-room', 'mass-follows-page');

    const flushUpdates = () => {
      if (updateQueueRef.current.size === 0) return;
      
      setTasks(prevTasks => {
        const updated = [...prevTasks];
        let changed = false;
        
        updateQueueRef.current.forEach((update, taskId) => {
          const index = updated.findIndex(t => String(t._id) === String(taskId));
          if (index >= 0) {
            updated[index] = {
              ...updated[index],
              ...update,
              progress: {
                ...updated[index].progress,
                ...(update.progress || {})
              },
              ...(update.accountStatus && { accountStatus: update.accountStatus })
            };
            changed = true;
          }
        });
        
        updateQueueRef.current.clear();
        return changed ? updated : prevTasks;
      });
      
      updateTimeoutRef.current = null;
    };

    socket.on('mass-follow-progress', (data) => {
      const taskIdKey = String(data.taskId);
      const existing = updateQueueRef.current.get(taskIdKey) || {};
      updateQueueRef.current.set(taskIdKey, {
        ...existing,
        progress: {
          ...existing.progress,
          ...data.progress
        },
        ...(data.status && { status: data.status })
      });
      
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(flushUpdates, 300);
    });

    socket.on('mass-follow-task-updated', (data) => {
      const taskIdStr = String(data.taskId);
      setTasks(prevTasks => {
        const index = prevTasks.findIndex(t => String(t._id) === taskIdStr);
        if (index >= 0) {
          const updated = [...prevTasks];
          updated[index] = {
            ...updated[index],
            ...(data.status != null && { status: data.status }),
            ...(data.progress && { progress: { ...updated[index].progress, ...data.progress } }),
            ...(data.accountStatus && { accountStatus: data.accountStatus })
          };
          return updated;
        }
        loadTasks();
        return prevTasks;
      });
    });

    return () => {
      socket.emit('leave-room', 'mass-follows-page');
      socket.off('mass-follow-progress');
      socket.off('mass-follow-task-updated');
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [socket]);

  const loadTasks = async () => {
    try {
      const response = await api.get('/mass-follow/tasks');
      setTasks(response.data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
      if (error.response?.status >= 500) {
        toast.error('Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await api.get('/mass-follow/accounts');
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/accounts/groups');
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const handleSelectAllAccounts = () => {
    const selectableAccounts = accounts.filter(acc => 
      acc.hasValidSession === true && acc.inUse !== true
    );
    
    const allSelected = selectableAccounts.length > 0 && 
      selectableAccounts.every(acc => formData.accountIds.includes(acc._id));
    
    if (allSelected) {
      setFormData({
        ...formData,
        accountIds: formData.accountIds.filter(id => 
          !selectableAccounts.map(acc => acc._id).includes(id)
        )
      });
    } else {
      const newIds = [...new Set([
        ...formData.accountIds,
        ...selectableAccounts.map(acc => acc._id)
      ])];
      setFormData({ ...formData, accountIds: newIds });
    }
  };

  const handleToggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleSelectGroup = (groupName) => {
    const groupAccounts = accounts.filter(acc => {
      if (!acc.group || acc.group.trim() !== groupName.trim()) return false;
      return acc.hasValidSession === true && acc.inUse !== true;
    });
    
    if (groupAccounts.length === 0) return;
    
    const groupAccountIds = groupAccounts.map(acc => acc._id);
    const allGroupSelected = groupAccountIds.every(id => formData.accountIds.includes(id));
    
    if (allGroupSelected) {
      setFormData({
        ...formData,
        accountIds: formData.accountIds.filter(id => !groupAccountIds.includes(id))
      });
    } else {
      setFormData({
        ...formData,
        accountIds: [...new Set([...formData.accountIds, ...groupAccountIds])]
      });
    }
  };

  const loadScrapedData = async () => {
    try {
      const response = await api.get('/mass-follow/scraped-data');
      setScrapedData(response.data);
    } catch (error) {
      console.error('Error loading scraped data:', error);
    }
  };

  const handleCreateTask = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Task name is required');
        return;
      }

      if (formData.accountIds.length === 0) {
        toast.error('Please select at least one account');
        return;
      }

      let payload = {
        name: formData.name,
        taskMode: formData.taskMode,
        accountIds: formData.accountIds,
        actionType: formData.taskMode === 'v2' ? 'follow' : formData.actionType,
        recipientSource: formData.recipientSource,
        settings: formData.settings
      };

      if (formData.recipientSource === 'manual') {
        const recipients = formData.manualRecipients
          .split('\n')
          .map(r => r.trim().replace('@', ''))
          .filter(r => r.length > 0);
        
        if (recipients.length === 0) {
          toast.error('Please add at least one recipient');
          return;
        }
        payload.manualRecipients = recipients;
      } else {
        if (!formData.scrapedDataTag) {
          toast.error('Please select scraped data source');
          return;
        }
        payload.scrapedDataTag = formData.scrapedDataTag;
      }

      await api.post('/mass-follow/tasks', payload);
      toast.success('Task created successfully');
      setShowCreateModal(false);
      resetForm();
      loadTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(error.response?.data?.error || 'Failed to create task');
    }
  };

  const handleEditTask = (task) => {
    setFormData({
      name: task.name,
      taskMode: task.taskMode || 'legacy',
      accountIds: task.accountIds.map(acc => acc._id || acc),
      actionType: task.actionType || 'follow',
      recipientSource: task.recipientSource,
      manualRecipients: task.manualRecipients ? task.manualRecipients.join('\n') : '',
      scrapedDataTag: task.scrapedDataTag || '',
      settings: {
        delayMin: task.settings?.delayMin || 30,
        delayMax: task.settings?.delayMax || 60,
        batchPauseEnabled: task.settings?.batchPauseEnabled !== false,
        followsPerBatchMin: task.settings?.followsPerBatchMin ?? task.settings?.followsPerBatch ?? 20,
        followsPerBatchMax: task.settings?.followsPerBatchMax ?? task.settings?.followsPerBatch ?? 20,
        batchPauseMin: task.settings?.batchPauseMin ?? task.settings?.batchPauseDuration ?? 600,
        batchPauseMax: task.settings?.batchPauseMax ?? task.settings?.batchPauseDuration ?? 600,
        restInterval: task.settings?.restInterval || 600,
        spamPauseDuration: task.settings?.spamPauseDuration || 3600,
        maxRequestsPerDayMin: task.settings?.maxRequestsPerDayMin ?? task.settings?.maxRequestsPerDay ?? 400,
        maxRequestsPerDayMax: task.settings?.maxRequestsPerDayMax ?? task.settings?.maxRequestsPerDay ?? 400,
        activeHoursPerDay: task.settings?.activeHoursPerDay ?? 12,
        unfollowAfterDays: task.settings?.unfollowAfterDays || null,
        unfollowAfterHours: task.settings?.unfollowAfterHours || null
      }
    });
    setEditingTask(task);
    setShowEditModal(true);
  };

  const handleUpdateTask = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Task name is required');
        return;
      }

      if (formData.accountIds.length === 0) {
        toast.error('Please select at least one account');
        return;
      }

      let payload = {
        name: formData.name,
        taskMode: formData.taskMode,
        accountIds: formData.accountIds,
        actionType: formData.taskMode === 'v2' ? 'follow' : formData.actionType,
        recipientSource: formData.recipientSource,
        settings: formData.settings
      };

      if (formData.recipientSource === 'manual') {
        const recipients = formData.manualRecipients
          .split('\n')
          .map(r => r.trim().replace('@', ''))
          .filter(r => r.length > 0);
        
        if (recipients.length === 0) {
          toast.error('Please add at least one recipient');
          return;
        }
        payload.manualRecipients = recipients;
      } else {
        if (!formData.scrapedDataTag) {
          toast.error('Please select scraped data source');
          return;
        }
        payload.scrapedDataTag = formData.scrapedDataTag;
      }

      const wasPaused = editingTask?.status === 'paused';
      
      await api.put(`/mass-follow/tasks/${editingTask._id}`, payload);
      toast.success('Task updated successfully');
      
      // If task was paused, resume it automatically
      if (wasPaused) {
        try {
          await api.post(`/mass-follow/tasks/${editingTask._id}/start`);
          toast.success('Task resumed from where it was paused');
        } catch (error) {
          console.error('Error resuming task:', error);
          toast.error('Task updated but failed to resume. Please start it manually.');
        }
      }
      
      setShowEditModal(false);
      setEditingTask(null);
      resetForm();
      loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(error.response?.data?.error || 'Failed to update task');
    }
  };

  const handleStartTask = async (taskId) => {
    setTasks(prevTasks => {
      const index = prevTasks.findIndex(t => t._id === taskId);
      if (index >= 0) {
        const updated = [...prevTasks];
        updated[index] = { ...updated[index], status: 'running' };
        return updated;
      }
      return prevTasks;
    });

    try {
      await api.post(`/mass-follow/tasks/${taskId}/start`);
      toast.success('Task started');
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error(error.response?.data?.error || 'Failed to start task');
      loadTasks();
    }
  };

  const handlePauseTask = async (taskId) => {
    setTasks(prevTasks => {
      const index = prevTasks.findIndex(t => t._id === taskId);
      if (index >= 0) {
        const updated = [...prevTasks];
        updated[index] = { ...updated[index], status: 'paused' };
        return updated;
      }
      return prevTasks;
    });

    try {
      await api.post(`/mass-follow/tasks/${taskId}/pause`);
      toast.success('Task paused');
    } catch (error) {
      console.error('Error pausing task:', error);
      toast.error(error.response?.data?.error || 'Failed to pause task');
      loadTasks();
    }
  };

  const handleStopTask = async (taskId) => {
    setTasks(prevTasks => {
      const index = prevTasks.findIndex(t => t._id === taskId);
      if (index >= 0) {
        const updated = [...prevTasks];
        updated[index] = { ...updated[index], status: 'stopped' };
        return updated;
      }
      return prevTasks;
    });

    try {
      await api.post(`/mass-follow/tasks/${taskId}/stop`);
      toast.success('Task stopped');
      loadTasks();
    } catch (error) {
      console.error('Error stopping task:', error);
      toast.error(error.response?.data?.error || 'Failed to stop task');
      loadTasks();
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await api.delete(`/mass-follow/tasks/${taskId}`);
      toast.success('Task deleted');
      loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.response?.data?.error || 'Failed to delete task');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      taskMode: 'legacy',
      accountIds: [],
      actionType: 'follow',
      recipientSource: 'scraped',
      manualRecipients: '',
      scrapedDataTag: '',
      settings: {
        delayMin: 30,
        delayMax: 60,
        batchPauseEnabled: true,
        followsPerBatchMin: 20,
        followsPerBatchMax: 20,
        batchPauseMin: 600,
        batchPauseMax: 600,
        restInterval: 600,
        spamPauseDuration: 3600,
        maxRequestsPerDayMin: 40,
        maxRequestsPerDayMax: 45,
        activeHoursPerDay: 12,
        unfollowAfterDays: null,
        unfollowAfterHours: null
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'stopped':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'pending':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getProgressPercentage = (task) => {
    if (!task.progress || task.progress.total === 0) return 0;
    const completed = (task.progress.completed || 0) + (task.progress.failed || 0) + (task.progress.skipped || 0);
    return Math.min(100, Math.round((completed / task.progress.total) * 100));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  // Only show to admins
  if (user?.role !== 'admin') {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="card-body text-center py-12">
            <UserPlusIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">Access Denied</h3>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Mass Follow feature is only available to administrators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-br from-blue-950 via-indigo-950 to-slate-900' 
          : 'bg-gradient-to-br from-blue-50 via-indigo-100 to-blue-100'
      }`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute -top-16 left-1/3 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-600/20' : 'bg-blue-300/40'
          }`} />
          <div className={`absolute -bottom-20 -right-16 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-indigo-600/15' : 'bg-indigo-300/30'
          }`} />
        </div>
        
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
              }`}>
                <UserPlusIcon className="w-8 h-8 text-white" />
              </div>
              
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Mass Follow
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Follow or unfollow users from scraped data
                </p>
              </div>
            </div>
            
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors shadow-lg shadow-blue-600/30"
            >
              <PlusIcon className="w-5 h-5" />
              Create Task
            </button>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {tasks.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-12">
              <UserPlusIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No tasks yet</h3>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Create your first mass follow task to start following/unfollowing users automatically.
              </p>
            </div>
          </div>
        ) : (
          tasks.map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              onStart={handleStartTask}
              onStop={handleStopTask}
              onPause={handlePauseTask}
              onEdit={handleEditTask}
              onDelete={handleDeleteTask}
              getStatusColor={getStatusColor}
              getProgressPercentage={getProgressPercentage}
              isDropdownOpen={openDropdowns.has(task._id.toString())}
              onToggleDropdown={() => handleToggleDropdown(task._id.toString())}
            />
          ))
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <TaskModal
          title="Create Mass Follow Task"
          formData={formData}
          setFormData={setFormData}
          accounts={accounts}
          scrapedData={scrapedData}
          onSubmit={handleCreateTask}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          submitLabel="Create Task"
          handleSelectAllAccounts={handleSelectAllAccounts}
          groups={groups}
          expandedGroups={expandedGroups}
          onToggleGroup={handleToggleGroup}
          onSelectGroup={handleSelectGroup}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && editingTask && (
        <TaskModal
          title="Edit Mass Follow Task"
          formData={formData}
          setFormData={setFormData}
          accounts={accounts}
          scrapedData={scrapedData}
          onSubmit={handleUpdateTask}
          onClose={() => {
            setShowEditModal(false);
            setEditingTask(null);
            resetForm();
          }}
          submitLabel="Update Task"
          handleSelectAllAccounts={handleSelectAllAccounts}
          groups={groups}
          expandedGroups={expandedGroups}
          onToggleGroup={handleToggleGroup}
          onSelectGroup={handleSelectGroup}
        />
      )}
    </div>
  );
};

// Task Card Component (memoized for performance)
const TaskCard = memo(({ task, onStart, onStop, onPause, onEdit, onDelete, getStatusColor, getProgressPercentage, isDropdownOpen, onToggleDropdown }) => {
  const [showDetails, setShowDetails] = useState(isDropdownOpen);

  useEffect(() => {
    setShowDetails(isDropdownOpen);
  }, [isDropdownOpen]);

  const getStatusBadgeColor = (status) => {
    if (status === 'active') return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    if (status === 'paused_batch') return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
    if (status === 'rate_limited') return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    if (status === 'daily_limit') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    if (status === 'completed') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    if (status === 'error') return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const progress = task.progress || { total: 0, completed: 0, failed: 0, skipped: 0 };
  const pending = progress.total - (progress.completed + progress.failed + progress.skipped);

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {task.name}
              </h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                {task.status}
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                task.actionType === 'follow' 
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                  : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
              }`}>
                {task.actionType}
              </span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                (task.taskMode || 'legacy') === 'v2'
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {(task.taskMode || 'legacy') === 'v2' ? 'V2' : 'Legacy'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <UserGroupIcon className="h-4 w-4" />
                {task.accountIds?.length || 0} account{(task.accountIds?.length || 0) !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <UserPlusIcon className="h-4 w-4" />
                {task.recipientSource === 'scraped' ? task.scrapedDataTag : 'Manual list'}
              </span>
            </div>
            {task.status === 'running' && task.accountStatus && task.accountStatus.length > 0 && (
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 flex-wrap">
                <span className="flex items-center gap-1">
                  <UserGroupIcon className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                  {task.accountStatus.length} total
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircleIcon className="h-3 w-3 text-green-600" />
                  {task.accountStatus.filter(acc => acc.status === 'active').length} active
                </span>
                {task.accountStatus.filter(acc => acc.status === 'paused_batch').length > 0 && (
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-3 w-3 text-purple-600" />
                    {task.accountStatus.filter(acc => acc.status === 'paused_batch').length} cooldown
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.status === 'daily_limit').length > 0 && (
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-3 w-3 text-yellow-600" />
                    {task.accountStatus.filter(acc => acc.status === 'daily_limit').length} daily limit
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.status === 'rate_limited').length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="h-3 w-3 text-red-600" />
                    {task.accountStatus.filter(acc => acc.status === 'rate_limited').length} rate limited
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.status === 'completed').length > 0 && (
                  <span className="flex items-center gap-1">
                    <CheckCircleIcon className="h-3 w-3 text-blue-600" />
                    {task.accountStatus.filter(acc => acc.status === 'completed').length} completed
                  </span>
                )}
                {/* Global Account.status counters - captcha, auth_expired, suspended */}
                {task.accountStatus.filter(acc => acc.accountId?.status === 'captcha').length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="h-3 w-3 text-orange-600" />
                    {task.accountStatus.filter(acc => acc.accountId?.status === 'captcha').length} captcha
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.accountId?.status === 'auth_expired').length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="h-3 w-3 text-orange-600" />
                    {task.accountStatus.filter(acc => acc.accountId?.status === 'auth_expired').length} auth expired
                  </span>
                )}
                {task.accountStatus.filter(acc => acc.accountId?.status === 'suspended').length > 0 && (
                  <span className="flex items-center gap-1">
                    <XCircleIcon className="h-3 w-3 text-red-600" />
                    {task.accountStatus.filter(acc => acc.accountId?.status === 'suspended').length} suspended
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {task.status === 'running' ? (
              <>
                <button
                  onClick={() => onPause(task._id)}
                  className="p-2 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-xl transition-colors"
                  title="Pause task"
                >
                  <PauseIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onStop(task._id)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  title="Stop task"
                >
                  <StopIcon className="h-5 w-5" />
                </button>
              </>
            ) : task.status === 'paused' || task.status === 'failed' ? (
              <>
                <button
                  onClick={() => onStart(task._id)}
                  className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl transition-colors"
                  title={task.status === 'failed' ? 'Retry task' : 'Resume task'}
                >
                  <PlayIcon className="h-5 w-5" />
                </button>
                {task.status !== 'failed' && (
                  <button
                    onClick={() => onStop(task._id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                    title="Stop task"
                  >
                    <StopIcon className="h-5 w-5" />
                  </button>
                )}
              </>
            ) : task.status === 'pending' || task.status === 'stopped' ? (
              <button
                onClick={() => onStart(task._id)}
                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                title="Start task"
              >
                <PlayIcon className="h-5 w-5" />
              </button>
            ) : task.status === 'completed' && pending > 0 ? (
              <button
                onClick={() => onStart(task._id)}
                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                title="Restart task (continue with pending)"
              >
                <PlayIcon className="h-5 w-5" />
              </button>
            ) : null}
            {(task.status === 'pending' || task.status === 'stopped' || task.status === 'paused' || task.status === 'failed') && (
              <button
                onClick={() => onEdit(task)}
                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors"
                title="Edit task"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => onDelete(task._id)}
              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
              title="Delete task"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        {(task.status === 'running' || task.status === 'completed' || task.status === 'paused') && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Progress: {progress.completed + progress.failed + progress.skipped} / {progress.total}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {getProgressPercentage(task)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${Math.min(100, getProgressPercentage(task))}%` }}
              />
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <CheckCircleIcon className="h-3 w-3 text-green-600" />
                {progress.completed} sent
              </span>
              <span className="flex items-center gap-1">
                <XCircleIcon className="h-3 w-3 text-red-600" />
                {progress.failed} failed
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3 text-yellow-600" />
                {progress.skipped} skipped
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="h-3 w-3 text-gray-400" />
                {pending > 0 ? pending : 0} pending
              </span>
            </div>
          </div>
        )}

        {/* Account Details Section */}
        {task.accountStatus && task.accountStatus.length > 0 && (
          <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <button
              onClick={onToggleDropdown}
              className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span>Account Details</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({task.accountStatus.length} accounts)
                </span>
              </span>
              {showDetails ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
            </button>
            
            {showDetails && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {task.accountStatus.map((accStatus) => (
                  <div
                    key={accStatus.accountId?._id || accStatus.accountId}
                    className="p-2 bg-gray-50 dark:bg-gray-700/50 rounded-2xl text-sm"
                  >
                    <div className="font-medium text-gray-900 dark:text-white truncate">
                      @{accStatus.accountId?.username || 'Unknown'}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeColor(accStatus.status)}`}>
                        {accStatus.status === 'paused_batch' ? 'Cooldown' : 
                         accStatus.status === 'rate_limited' ? 'Rate Limited' :
                         accStatus.status === 'daily_limit' ? 'Daily Limit' :
                         accStatus.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {accStatus.followsSent || 0} {task.actionType}s sent
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

// Task Modal Component (shared between Create and Edit)
const TaskModal = ({ title, formData, setFormData, accounts, scrapedData, onSubmit, onClose, submitLabel, handleSelectAllAccounts, groups = [], expandedGroups = new Set(), onToggleGroup, onSelectGroup }) => {
  const isV2 = formData.taskMode === 'v2';
  const selectedAccountCount = formData.accountIds.length;
  const dailyMin = Math.max(1, parseInt(formData.settings.maxRequestsPerDayMin, 10) || 40);
  const dailyMax = Math.max(dailyMin, parseInt(formData.settings.maxRequestsPerDayMax, 10) || dailyMin);
  const activeHours = Math.max(1, Math.min(24, parseInt(formData.settings.activeHoursPerDay, 10) || 12));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Task Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Task Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter task name"
            />
          </div>

          {/* Action Type */}
          {!isV2 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Action Type *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="follow"
                  checked={formData.actionType === 'follow'}
                  onChange={(e) => setFormData({ ...formData, actionType: e.target.value })}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Follow</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="unfollow"
                  checked={formData.actionType === 'unfollow'}
                  onChange={(e) => setFormData({ ...formData, actionType: e.target.value })}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Unfollow</span>
              </label>
            </div>
          </div>
          )}

          {/* Task Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Follow Mode *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, taskMode: 'legacy' })}
                className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                  !isV2
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">Legacy Follow</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Manual delay/batch settings.</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({
                  ...formData,
                  taskMode: 'v2',
                  actionType: 'follow',
                  settings: {
                    ...formData.settings,
                    maxRequestsPerDayMin: formData.settings.maxRequestsPerDayMin || 40,
                    maxRequestsPerDayMax: formData.settings.maxRequestsPerDayMax || 45,
                    activeHoursPerDay: formData.settings.activeHoursPerDay || 12,
                  }
                })}
                className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                  isV2
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">V2 Follow</div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">Auto browser + auto-paced scheduling.</div>
              </button>
            </div>
          </div>

          {/* Accounts Selection - Same as MassLikes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Accounts *
              </label>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formData.accountIds.length} of {accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true).length} selected
              </span>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3">
              {accounts.length > 0 && (
                <label className="flex items-center gap-2 cursor-pointer pb-2 border-b border-gray-200 dark:border-gray-700 mb-2">
                  <input
                    type="checkbox"
                    checked={accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true).length > 0 && 
                             accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true)
                               .every(acc => formData.accountIds.includes(acc._id))}
                    onChange={handleSelectAllAccounts}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select All Available
                  </span>
                </label>
              )}
              {accounts.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 p-2">
                  No accounts found. Please add accounts first.
                </p>
              ) : (
                <>
                  {/* Groups Section - Same structure as MassLikes */}
                  {(() => {
                    const accountGroups = [...new Set(accounts
                      .map(acc => acc.group)
                      .filter(g => g != null && String(g).trim() !== '')
                      .map(g => String(g).trim()))];
                    
                    if (accountGroups.length === 0) return null;
                    
                    return (
                      <div className="space-y-1 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                          Groups
                        </div>
                        {accountGroups.sort().map((groupName) => {
                          const groupAccounts = accounts.filter(acc => {
                            if (!acc.group) return false;
                            return String(acc.group).trim() === groupName;
                          });
                          const selectableGroupAccounts = groupAccounts.filter(acc => 
                            acc.hasValidSession === true && acc.inUse !== true
                          );
                          const groupAccountIds = selectableGroupAccounts.map(acc => acc._id);
                          const allGroupSelected = selectableGroupAccounts.length > 0 && 
                            groupAccountIds.every(id => formData.accountIds.includes(id));
                          const isExpanded = expandedGroups.has(groupName);
                          
                          return (
                            <div key={groupName} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center flex-1 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => onToggleGroup && onToggleGroup(groupName)}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                  >
                                    {isExpanded ? (
                                      <ChevronDownIcon className="h-4 w-4" />
                                    ) : (
                                      <ChevronDownIcon className="h-4 w-4 transform -rotate-90" />
                                    )}
                                  </button>
                                  <input
                                    type="checkbox"
                                    checked={allGroupSelected}
                                    onChange={() => onSelectGroup && onSelectGroup(groupName)}
                                    disabled={selectableGroupAccounts.length === 0}
                                    className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                                      selectableGroupAccounts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                  />
                                  <span className={`text-sm font-medium ${
                                    selectableGroupAccounts.length === 0
                                      ? 'text-gray-400 dark:text-gray-500'
                                      : 'text-gray-700 dark:text-gray-300'
                                  }`}>
                                    {groupName} ({selectableGroupAccounts.length}/{groupAccounts.length})
                                  </span>
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="ml-6 space-y-1 mt-1">
                                  {groupAccounts.map((account) => {
                                    const hasValidSession = account.hasValidSession === true;
                                    const inUse = account.inUse === true;
                                    const isDisabled = !hasValidSession || inUse;
                                    return (
                                      <label key={account._id} className="flex items-center justify-between">
                                        <div className="flex items-center flex-1">
                                          <input
                                            type="checkbox"
                                            checked={formData.accountIds.includes(account._id)}
                                            disabled={isDisabled}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setFormData({
                                                  ...formData,
                                                  accountIds: [...formData.accountIds, account._id]
                                                });
                                              } else {
                                                setFormData({
                                                  ...formData,
                                                  accountIds: formData.accountIds.filter(id => id !== account._id)
                                                });
                                              }
                                            }}
                                            className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                                              isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                          />
                                          <span className={`ml-2 text-sm ${
                                            isDisabled 
                                              ? 'text-gray-400 dark:text-gray-500' 
                                              : 'text-gray-700 dark:text-gray-300'
                                          }`}>
                                            @{account.username}
                                          </span>
                                        </div>
                                        {!hasValidSession && (
                                          <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                            ⚠️ No Session
                                          </span>
                                        )}
                                        {hasValidSession && inUse && (
                                          <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used">
                                            ⚠️ In Use
                                          </span>
                                        )}
                                        {hasValidSession && !inUse && (
                                          <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session">
                                            ✓ Ready
                                          </span>
                                        )}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  
                  {/* Ungrouped Accounts - Same structure as MassLikes */}
                  {(() => {
                    const ungroupedAccounts = accounts.filter(acc => !acc.group || acc.group.trim() === '');
                    if (ungroupedAccounts.length === 0) return null;
                    
                    return (
                      <div className="space-y-1">
                        {(() => {
                          const accountGroups = [...new Set(accounts
                            .map(acc => acc.group)
                            .filter(g => g != null && String(g).trim() !== ''))];
                          if (accountGroups.length > 0) {
                            return (
                              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                Ungrouped
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {ungroupedAccounts.map((account) => {
                          const hasValidSession = account.hasValidSession === true;
                          const inUse = account.inUse === true;
                          const isDisabled = !hasValidSession || inUse;
                          return (
                            <label key={account._id} className="flex items-center justify-between">
                              <div className="flex items-center flex-1">
                                <input
                                  type="checkbox"
                                  checked={formData.accountIds.includes(account._id)}
                                  disabled={isDisabled}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        accountIds: [...formData.accountIds, account._id]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        accountIds: formData.accountIds.filter(id => id !== account._id)
                                      });
                                    }
                                  }}
                                  className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                                    isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                  }`}
                                />
                                <span className={`ml-2 text-sm ${
                                  isDisabled 
                                    ? 'text-gray-400 dark:text-gray-500' 
                                    : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                  @{account.username}
                                </span>
                              </div>
                              {!hasValidSession && (
                                <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                  ⚠️ No Session
                                </span>
                              )}
                              {hasValidSession && inUse && (
                                <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used">
                                  ⚠️ In Use
                                </span>
                              )}
                              {hasValidSession && !inUse && (
                                <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session">
                                  ✓ Ready
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          {/* Recipient Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recipient Source *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="scraped"
                  checked={formData.recipientSource === 'scraped'}
                  onChange={(e) => setFormData({ ...formData, recipientSource: e.target.value })}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Scraped Data</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="manual"
                  checked={formData.recipientSource === 'manual'}
                  onChange={(e) => setFormData({ ...formData, recipientSource: e.target.value })}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Manual List</span>
              </label>
            </div>
          </div>

          {/* Scraped Data Selection */}
          {formData.recipientSource === 'scraped' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Scraped Data Source *
              </label>
              <select
                value={formData.scrapedDataTag}
                onChange={(e) => setFormData({ ...formData, scrapedDataTag: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a tag...</option>
                {scrapedData.tags?.map((tag) => (
                  <option key={tag.tag} value={tag.tag}>
                    {tag.tag} ({tag.totalCount?.toLocaleString() || 0} users)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Manual Recipients */}
          {formData.recipientSource === 'manual' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Recipients (one per line) *
              </label>
              <textarea
                value={formData.manualRecipients}
                onChange={(e) => setFormData({ ...formData, manualRecipients: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={5}
                placeholder="@username1&#10;@username2&#10;@username3"
              />
            </div>
          )}

          {/* Settings */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Settings</h3>
            {isV2 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Min follows/day</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.settings.maxRequestsPerDayMin}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, maxRequestsPerDayMin: parseInt(e.target.value, 10) || 40 }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Max follows/day</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.settings.maxRequestsPerDayMax}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, maxRequestsPerDayMax: parseInt(e.target.value, 10) || 45 }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Active hours/day</label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={formData.settings.activeHoursPerDay || 12}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: { ...formData.settings, activeHoursPerDay: parseInt(e.target.value, 10) || 12 }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <V2SendDistributionPreview
                  accountCount={selectedAccountCount}
                  dailyMin={dailyMin}
                  dailyMax={dailyMax}
                  activeHoursPerDay={activeHours}
                />
              </div>
            ) : (
            <>
            <div className="mb-4 flex items-center gap-2">
              <input
                id="mf-remove-batch-pause"
                type="checkbox"
                checked={formData.settings.batchPauseEnabled === false}
                onChange={(e) => setFormData({
                  ...formData,
                  settings: { ...formData.settings, batchPauseEnabled: !e.target.checked }
                })}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <label htmlFor="mf-remove-batch-pause" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                Remove batch pauses (continuous follows until daily cap; batch size / pause fields disabled)
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Min Delay (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.delayMin}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, delayMin: parseInt(e.target.value) || 30 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Max Delay (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.delayMax}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, delayMax: parseInt(e.target.value) || 60 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Min follows per batch
                </label>
                <input
                  type="number"
                  value={formData.settings.followsPerBatchMin}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, followsPerBatchMin: parseInt(e.target.value, 10) || 1 }
                  })}
                  disabled={formData.settings.batchPauseEnabled === false}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Max follows per batch
                </label>
                <input
                  type="number"
                  value={formData.settings.followsPerBatchMax}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, followsPerBatchMax: parseInt(e.target.value, 10) || 1 }
                  })}
                  disabled={formData.settings.batchPauseEnabled === false}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  min="1"
                />
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Random count per batch when batch pauses are on.</p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Batch pause min (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.batchPauseMin}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, batchPauseMin: parseInt(e.target.value, 10) || 60 }
                  })}
                  disabled={formData.settings.batchPauseEnabled === false}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  min="60"
                />
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Random pause after each batch; set equal to max for fixed length.</p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Batch pause max (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.batchPauseMax}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, batchPauseMax: parseInt(e.target.value, 10) || 60 }
                  })}
                  disabled={formData.settings.batchPauseEnabled === false}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  min="60"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Rest Interval (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.restInterval}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, restInterval: parseInt(e.target.value) || 600 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Spam Pause Duration (seconds)
                </label>
                <input
                  type="number"
                  value={formData.settings.spamPauseDuration}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, spamPauseDuration: parseInt(e.target.value) || 3600 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Pause duration when spam is detected</p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Min follows per day (per account)
                </label>
                <input
                  type="number"
                  value={formData.settings.maxRequestsPerDayMin}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, maxRequestsPerDayMin: parseInt(e.target.value, 10) || 0 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Max follows per day (per account)
                </label>
                <input
                  type="number"
                  value={formData.settings.maxRequestsPerDayMax}
                  onChange={(e) => setFormData({
                    ...formData,
                    settings: { ...formData.settings, maxRequestsPerDayMax: parseInt(e.target.value, 10) || 0 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Random cap per account when the task starts. Use 0 and 0 for unlimited.</p>
              </div>
            </div>
            
            {/* Unfollow Settings - Only show for unfollow tasks */}
            {formData.actionType === 'unfollow' && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Unfollow Timing</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Unfollow After (Days)
                    </label>
                    <input
                      type="number"
                      value={formData.settings.unfollowAfterDays || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: {
                          ...formData.settings,
                          unfollowAfterDays: e.target.value ? parseInt(e.target.value) : null,
                          unfollowAfterHours: null // Clear hours if days is set
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="1"
                      placeholder="e.g., 7"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Only unfollow recipients followed X+ days ago. Leave empty to unfollow all.</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Unfollow After (Hours)
                    </label>
                    <input
                      type="number"
                      value={formData.settings.unfollowAfterHours || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        settings: {
                          ...formData.settings,
                          unfollowAfterHours: e.target.value ? parseInt(e.target.value) : null,
                          unfollowAfterDays: null // Clear days if hours is set
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="1"
                      placeholder="e.g., 24"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Only unfollow recipients followed X+ hours ago. Leave empty to unfollow all.</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                  Note: Only one of these fields should be set. If both are set, days takes precedence.
                </p>
              </div>
            )}
            </>
            )}
          </div>

        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MassFollows;

